import React, { Fragment, useState } from 'react';
import { connect } from 'react-redux';
import dynamic from 'next/dynamic';
import { arrayOf, node, oneOf, string, bool } from 'prop-types';
import classnames from 'classnames';
import Search from 'components/Icons/Search';
import { NAV_LINKS } from 'constants/links';
import {
  POSTUP_INLINE_MOBILE_IDS,
  HAS_ACCOUNT,
  HAS_SEARCH,
  HAS_RECIPE_BOX,
  HAS_NEWSLETTERS
} from 'constants/site';
import { SITE_LOGO, RECIPE_BOX_LOGO } from 'constants/logos';
import LogoLink from 'components/LogoLink';
import { isMobile } from 'util/viewport';
import siteStyles from 'styles/SiteHeader';
import SiteHeaderNav from './SiteHeaderNav';
import SiteHeaderNewsletter from './SiteHeaderNewsletter';
import styles from './styles';

export const AccountLink = dynamic(() => import('./SiteHeaderAccountLink'), {
  ssr: false,
  loading: () => <div style={{ width: 20 }} />
});

export const SiteHeaderRecipeBox = dynamic(
  () => import('./SiteHeaderRecipeBox'),
  {
    ssr: false,
    loading: () => <div style={{ width: 20 }} />
  }
);

const TypeAheadSearch = dynamic(() => import('components/TypeAheadSearch'), {
  ssr: false,
  loading: () => null
});

export function SiteHeader(props) {
  const { currentPath, logo, variant, flips, showNav } = props;
  const [activeTab, setActiveTab] = useState(null);
  const [
    isTypeAheadMobileSearchVisible,
    setIsTypeAheadMobileSearchVisible
  ] = useState(false);

  const siteHeaderClasses = classnames('SiteHeader', {
    [`SiteHeader--${variant}`]: variant,
    'SiteHeader--no-nav': showNav === false || NAV_LINKS.length === 0
  });

  const siteHeaderIconClasses = classnames(
    'SiteHeader__user',
    'SiteHeader__icon',
    {
      [`SiteHeader__icon--${variant}`]: variant
    }
  );

  const useMobileTypeAheadSearch =
    isMobile(1024) && isTypeAheadMobileSearchVisible;

  return (
    <Fragment>
      <header className={siteHeaderClasses} data-page-path={currentPath}>
        {useMobileTypeAheadSearch ? (
          <div className="SiteHeader__searchBoxContainer">
            <TypeAheadSearch />
            <div className="SiteHeader__cancelBtnContainer">
              <button
                onClick={e => {
                  e.preventDefault();
                  setIsTypeAheadMobileSearchVisible(false);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <Fragment>
            <div className="SiteHeader__logo">
              <LogoLink logo={logo} href="/" />
            </div>
            {variant === 'recipeBox' && (
              <span className="SiteHeader__recipeBoxHeader">
                {RECIPE_BOX_LOGO}
              </span>
            )}
            <div className="SiteHeader__actions">
              {HAS_NEWSLETTERS && variant !== 'recipeBox' && (
                <SiteHeaderNewsletter />
              )}

              {HAS_RECIPE_BOX && variant !== 'recipeBox' && (
                <SiteHeaderRecipeBox />
              )}

              {HAS_ACCOUNT && (
                <div className="SiteHeader__user SiteHeader__icon">
                  <AccountLink />
                </div>
              )}

              {HAS_SEARCH &&
                variant !== 'searchPage' &&
                variant !== 'recipeBox' && (
                  <Fragment>
                    <button
                      className="SiteHeader__search-button SiteHeader__icon"
                      onClick={e => {
                        e.preventDefault();
                        setIsTypeAheadMobileSearchVisible(true);
                      }}
                      aria-label="Search"
                    >
                      <Search />
                    </button>
                    <div className="SiteHeader__search">
                      <TypeAheadSearch />
                    </div>
                  </Fragment>
                )}
            </div>
          </Fragment>
        )}

        {showNav && NAV_LINKS.length > 0 && (
          <nav className="SiteHeader__nav">
            <SiteHeaderNav
              navTree={NAV_LINKS}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />
          </nav>
        )}
      </header>
      <style jsx>{styles}</style>
      <style jsx>{siteStyles}</style>
    </Fragment>
  );
}

SiteHeader.defaultProps = {
  currentPath: undefined,
  logo: SITE_LOGO,
  variant: undefined,
  flips: [],
  showNav: true
};

SiteHeader.propTypes = {
  currentPath: string,
  logo: node,
  variant: oneOf([
    'centered',
    'centeredWhite',
    'knockout',
    'narrow-logo',
    'searchPage',
    'recipeBox',
    'wide-logo'
  ]),
  flips: arrayOf(string),
  showNav: bool
};

const mapStateToProps = state => {
  const { app } = state;
  return {
    flips: app.flips
  };
};

export default connect(mapStateToProps)(SiteHeader);
